import { useEffect, useState } from 'react';
import api from '../api/apiClient';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import useAuth from '../hooks/useAuth';
import SortFilter, { sortItems } from '../components/SortFilter';

const emptyDocument = { student_id: '', document_type: 'popia', title: '', status: 'pending', expires_at: '' };

const resolveFileUrl = (url, path) => {
  const target = url || path;
  if (!target) return null;
  let clean = String(target).trim();
  if (!clean || ['null', 'undefined', 'none', 'false', '0'].includes(clean.toLowerCase())) return null;
  if (clean.startsWith('http://') || clean.startsWith('https://')) return clean;

  const r2Url = process.env.REACT_APP_R2_PUBLIC_URL || 'https://pub-8403cb60474c4e039483cf5333503261.r2.dev';
  clean = clean.replace(/^\/+/, '').replace(/^uploads\//, '');
  if (!clean.includes('/')) {
    clean = `student/${clean}`;
  }
  return `${r2Url}/${clean}`;
};

export default function CompliancePage() {
  const { currentBranch } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [students, setStudents] = useState([]);
  const [sortOption, setSortOption] = useState('newest');
  const [form, setForm] = useState(emptyDocument);
  const [file, setFile] = useState(null);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    load();
  }, [currentBranch]);

  async function load() {
    try {
      const studentParams = {};
      if (currentBranch) {
        studentParams.branch_id = currentBranch.branch_id;
      }
      const [documentResponse, studentResponse] = await Promise.all([
        api.get('/admin/compliance-documents'),
        api.get('/students', { params: studentParams }),
      ]);
      setDocuments(documentResponse.data.data || []);
      setStudents(studentResponse.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load compliance records');
    }
  }

  const uploadDocument = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');
    try {
      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value) payload.append(key, value);
      });
      if (file) payload.append('document', file);
      await api.post('/admin/compliance-documents', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(emptyDocument);
      setFile(null);
      setIsDocumentModalOpen(false);
      setSuccessMessage('Compliance document saved');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save document');
    }
  };

  const updateStatus = async (id, newStatus) => {
    setError('');
    setSuccessMessage('');
    try {
      await api.put(`/admin/compliance-documents/${id}`, { status: newStatus });
      setSuccessMessage(`Document status updated to ${newStatus}`);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update document status');
    }
  };

  const deleteDocument = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document record?')) return;
    setError('');
    setSuccessMessage('');
    try {
      await api.delete(`/admin/compliance-documents/${id}`);
      setSuccessMessage('Document deleted successfully');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to delete document');
    }
  };

  const sortedDocuments = sortItems(documents, sortOption, 'title', 'created_at');

  return (
    <div>
      <PageHeader
        title="Compliance & Document Management"
        subtitle="Manage POPIA, indemnity, photo consent, and medical documents"
        action={
          <button type="button" className="primary-button" onClick={() => setIsDocumentModalOpen(true)} style={{ whiteSpace: 'nowrap' }}>
            + Save Document
          </button>
        }
      />
      {successMessage && <div className="success-message">{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="page-header-row" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Compliance Documents</h3>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          <SortFilter sortOption={sortOption} onSortChange={setSortOption} />
        </div>
      </div>

      <Modal isOpen={isDocumentModalOpen} onClose={() => setIsDocumentModalOpen(false)} title="Save Document">
        <form className="inline-form" onSubmit={uploadDocument}>
          <select value={form.student_id} onChange={(e) => setForm((p) => ({ ...p, student_id: e.target.value }))}>
            <option value="">General document</option>
            {students.map((student) => <option key={student.id} value={student.id}>{student.full_name}</option>)}
          </select>
          <select value={form.document_type} onChange={(e) => setForm((p) => ({ ...p, document_type: e.target.value }))}>
            <option value="popia">POPIA consent</option>
            <option value="indemnity">Indemnity</option>
            <option value="photo_consent">Photo consent</option>
            <option value="medical">Medical</option>
            <option value="other">Other</option>
          </select>
          <input placeholder="Title" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} required />
          <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
            <option value="pending">pending</option>
            <option value="approved">approved</option>
            <option value="expired">expired</option>
            <option value="archived">archived</option>
          </select>
          <input type="date" value={form.expires_at} onChange={(e) => setForm((p) => ({ ...p, expires_at: e.target.value }))} />
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={() => setIsDocumentModalOpen(false)}>Cancel</button>
            <button type="submit" className="primary-button">Save Document</button>
          </div>
        </form>
      </Modal>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Student</th>
              <th>Type</th>
              <th>Status (Click to Change)</th>
              <th>Expires</th>
              <th>File</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedDocuments.map((doc) => {
              const fileLink = resolveFileUrl(doc.file_url, doc.file_path);
              return (
                <tr key={doc.id}>
                  <td style={{ fontWeight: 600 }}>{doc.title}</td>
                  <td>{doc.student_name || 'General'}</td>
                  <td><span style={{ textTransform: 'capitalize' }}>{doc.document_type}</span></td>
                  <td>
                    <select
                      value={doc.status}
                      onChange={(e) => updateStatus(doc.id, e.target.value)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: 700,
                        border: '1px solid #cbd5e1',
                        cursor: 'pointer',
                        outline: 'none',
                        background: doc.status === 'approved' ? '#d1fae5' : (doc.status === 'pending' ? '#fef3c7' : (doc.status === 'expired' ? '#fee2e2' : '#f3f4f6')),
                        color: doc.status === 'approved' ? '#065f46' : (doc.status === 'pending' ? '#92400e' : (doc.status === 'expired' ? '#991b1b' : '#374151'))
                      }}
                    >
                      <option value="pending">pending</option>
                      <option value="approved">approved</option>
                      <option value="expired">expired</option>
                      <option value="archived">archived</option>
                    </select>
                  </td>
                  <td>{doc.expires_at ? new Date(doc.expires_at).toLocaleDateString() : '-'}</td>
                  <td>{fileLink ? <a href={fileLink} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>View</a> : '-'}</td>
                  <td>
                    <button
                      type="button"
                      onClick={() => deleteDocument(doc.id)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        border: 'none',
                        background: '#fee2e2',
                        color: '#dc2626',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
